
import React, { useState, useMemo, useEffect } from 'react';
import { SearchIcon } from './icons/SearchIcon';
import { BookOpenIcon } from './icons/BookOpenIcon';
import { FAQItem } from './FAQItem';
import { ChevronDownIcon } from './icons/ChevronDownIcon';
import { performSemanticSearch } from '../services/vectorService';
import { SparkleIcon } from './icons/SparkleIcon';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { InterestSimulator } from './InterestSimulator';
import { TrendingUpIcon } from './icons/TrendingUpIcon';
import { ArrowRightIcon } from './icons/ArrowRightIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import DocumentIntelligence from './DocumentIntelligence';

// --- Icons ---

const LoanIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125-1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const ProgramIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0l-.07.042m15.622 0l-.07-.042m-15.482 0A49.928 49.928 0 0112 10.5c3.252 0 6.17-.644 8.706-1.727m-17.412 0h17.412v1.511h-17.412v-1.511z" />
    </svg>
);

// --- Data for the Knowledge Base ---

const allResources = [
  {
    id: 'direct-loans',
    category: 'Loan Type',
    title: 'Federal Direct Loans',
    description: 'The most common federal loans from the U.S. Dept. of Education. Includes Subsidized, Unsubsidized, PLUS, and Consolidation loans.',
    details: [
      { type: 'heading', text: 'Key Characteristics' },
      { type: 'bullet', text: 'Eligible for PSLF, Teacher Loan Forgiveness, and IDR forgiveness.' },
      { type: 'bullet', text: 'Fixed interest rates for the life of the loan.' },
      { type: 'bullet', text: 'Offer flexible repayment plans including Standard, Graduated, Extended, and IDR.' },
      
      { type: 'heading', text: 'Repayment & Forgiveness' },
      { type: 'link', text: 'Explore Repayment Plans', href: 'https://studentaid.gov/manage-loans/repayment/plans' },
      { type: 'link', text: 'Learn About PSLF', href: 'https://studentaid.gov/manage-loans/forgiveness-cancellation/public-service' },
      { type: 'link', text: 'About Teacher Loan Forgiveness', href: 'https://studentaid.gov/teach-forgiveness' },
      
      { type: 'heading', text: 'Management & Consolidation' },
      { type: 'bullet', text: 'Can be consolidated for simplified payments and expanded eligibility.'},
      { type: 'link', text: 'How to Consolidate Loans', href: 'https://studentaid.gov/manage-loans/consolidation' },
      { type: 'bullet', text: 'Managed by official federal loan servicers.' },
      { type: 'link', text: 'Find Your Loan Servicer', href: 'https://studentaid.gov/manage-loans/repayment/servicers' },
      
      { type: 'heading', text: 'Important Alerts' },
      { type: 'bullet', text: 'Annual certification for PSLF and IDR is critical. Missing deadlines can affect eligibility.'},
      { type: 'link', text: 'Check for Latest Updates', href: 'https://studentaid.gov/announcements-events' },
    ],
    lastUpdated: 'October 26, 2025'
  },
  {
    id: 'pslf',
    category: 'Forgiveness Program',
    title: 'Public Service Loan Forgiveness (PSLF)',
    description: 'Forgives the remaining balance on your Direct Loans after you have made 120 qualifying monthly payments under a qualifying repayment plan while working full-time for a qualifying employer.',
    details: [
      { type: 'heading', text: 'Eligibility Highlights' },
      { type: 'bullet', text: 'Work full-time for a U.S. federal, state, local, or tribal government or a non-profit organization.' },
      { type: 'bullet', text: 'Have Direct Loans and repay them under an Income-Driven Repayment (IDR) plan.' },
      { type: 'bullet', text: 'Make 120 qualifying payments.' },
      { type: 'heading', text: 'Official Source' },
      { type: 'link', text: 'Use the PSLF Help Tool', href: 'https://studentaid.gov/pslf/' },
      { type: 'download', text: 'Download PSLF Fact Sheet', href: '#' }
    ],
    lastUpdated: 'October 26, 2025'
  },
  {
    id: 'idr',
    category: 'Forgiveness Program',
    title: 'Income-Driven Repayment (IDR) & SAVE Plan',
    description: 'IDR plans set your monthly student loan payment at an amount that is intended to be affordable based on your income and family size.',
    details: [
      { type: 'heading', text: 'Key Plans' },
      { type: 'bullet', text: 'SAVE (Saving on a Valuable Education): Generally the most affordable plan for most borrowers.' },
      { type: 'bullet', text: 'PAYE, REPAYE, ICR: Older plans with different terms.' },
      { type: 'heading', text: 'Forgiveness Component' },
      { type: 'bullet', text: 'Any remaining loan balance is forgiven after 20-25 years of payments.' },
      { type: 'heading', text: 'Official Source' },
      { type: 'link', text: 'IDR Information at StudentAid.gov', href: 'https://studentaid.gov/manage-loans/repayment/plans/income-driven' },
    ],
    lastUpdated: 'October 15, 2025'
  },
  {
    id: 'tpd-discharge',
    category: 'Forgiveness Program',
    title: 'Total and Permanent Disability (TPD) Discharge',
    description: "Relieves you from having to repay your federal student loans if you're totally and permanently disabled.",
    details: [
        { type: 'heading', text: 'How to Qualify' },
        { type: 'bullet', text: 'Provide documentation from the U.S. Department of Veterans Affairs (VA), the Social Security Administration (SSA), or a physician.' },
        { type: 'heading', text: 'Official Source' },
        { type: 'link', text: 'Learn about TPD Discharge', href: 'https://disabilitydischarge.com/' },
    ],
    lastUpdated: 'October 20, 2025'
  },
  {
    id: 'teacher-forgiveness',
    category: 'Forgiveness Program',
    title: 'Teacher Loan Forgiveness',
    description: 'Provides up to $17,500 in forgiveness for teachers who work for five full and consecutive academic years in a low-income school.',
     details: [
        { type: 'heading', text: 'Eligibility Highlights' },
        { type: 'bullet', text: 'Must be a highly qualified teacher.' },
        { type: 'bullet', text: 'Time spent teaching for this program does not count toward PSLF.' },
        { type: 'heading', text: 'Official Source' },
        { type: 'link', text: 'Teacher Forgiveness Info', href: 'https://studentaid.gov/teach-forgiveness' },
    ],
    lastUpdated: 'October 1, 2025'
  },
  {
    id: 'military-forgiveness',
    category: 'Forgiveness Program',
    title: 'Military Service Forgiveness & Benefits',
    description: 'Service members have access to unique student loan benefits and forgiveness options through the Department of Defense and other federal programs.',
    details: [
        { type: 'heading', text: 'Key Benefits' },
        { type: 'bullet', text: 'Interest rate caps under the Servicemembers Civil Relief Act (SCRA).' },
        { type: 'bullet', text: 'Deferment options during active duty.' },
        { type: 'bullet', text: 'Potential for Department of Defense (DoD) loan repayment programs.' },
        { type: 'bullet', text: 'Time in service may count towards PSLF.' },
        { type: 'heading', text: 'Official Source' },
        { type: 'link', text: 'Federal Student Aid for Military Members', href: 'https://studentaid.gov/manage-loans/forgiveness-cancellation/military' },
    ],
    lastUpdated: 'September 30, 2025'
  },
  {
    id: 'policy-updates',
    category: 'Forgiveness Program',
    title: 'Borrower Defense & Policy Changes',
    description: 'Regulations allow for loan discharge if a school misled you or engaged in misconduct. Policy changes can affect eligibility for various programs.',
    details: [
        { type: 'heading', text: 'Borrower Defense to Repayment' },
        { type: 'bullet', text: 'Applies if your school violated certain laws, leading to financial harm.' },
        { type: 'heading', text: 'Staying Updated' },
        { type: 'bullet', text: 'Forgiveness rules can change. It is vital to check official sources regularly.' },
        { type: 'link', text: 'Apply for Borrower Defense', href: 'https://studentaid.gov/borrower-defense/' },
        { type: 'link', text: 'Official Announcements', href: 'https://studentaid.gov/announcements-events' },
    ],
    lastUpdated: 'October 26, 2025'
  },
  {
    id: 'ffel-perkins',
    category: 'Loan Type',
    title: 'FFEL & Perkins Loans',
    description: 'Older federal loans issued by private banks (FFEL) or schools (Perkins), but guaranteed by the federal government.',
    details: [
        { type: 'heading', text: 'Important Note' },
        { type: 'bullet', text: 'These loans often must be consolidated into a Direct Consolidation Loan to qualify for PSLF and most IDR plans.' },
        { type: 'heading', text: 'Official Source' },
        { type: 'link', text: 'Learn about consolidation', href: 'https://studentaid.gov/manage-loans/consolidation' },
    ],
    lastUpdated: 'September 5, 2025'
  },
   {
    id: 'private-loans',
    category: 'Loan Type',
    title: 'Private Student Loans',
    description: 'Loans made by a private lender, such as a bank or credit union. These are not eligible for federal forgiveness programs.',
    details: [
        { type: 'heading', text: 'Key Characteristics' },
        { type: 'bullet', text: 'Terms and conditions are set by the lender, not the government.' },
        { type: 'bullet', text: 'Options are generally limited to refinancing with a different private lender.' },
    ],
    lastUpdated: 'August 12, 2025'
  },
];

const faqs = [
  { q: "What's the difference between forgiveness, cancellation, and discharge?", a: "They generally mean the same thing—you’re no longer required to make payments on your loans. The terms are often used for different reasons for forgiveness (e.g., discharge for disability, forgiveness for public service)." },
  { q: "Do I have to pay taxes on forgiven student loan debt?", a: "Under current federal law (through 2025), debt forgiven through federal programs is not considered taxable income. State tax laws may vary." },
  { q: "Can I consolidate private and federal loans together?", a: "No, you cannot consolidate private student loans into a federal Direct Consolidation Loan. They must be kept separate." },
];

// --- Sub-components for Rendering ---

const FeaturedToolCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <div className="col-span-full bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white shadow-lg relative overflow-hidden group cursor-pointer mb-8" onClick={onClick}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-16 -mt-16 transform group-hover:scale-110 transition-transform duration-500"></div>
        <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
                <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded uppercase tracking-wide">New Tool</span>
            </div>
            <h3 className="text-2xl font-bold mb-2">Stop the "Interest Trap"</h3>
            <p className="text-green-100 max-w-2xl mb-6">
                See exactly how much money you are losing to negative amortization and calculate your "Years Counted" towards forgiveness. Don't leave money on the table.
            </p>
            <button className="bg-white text-green-700 hover:bg-green-50 font-bold py-2 px-6 rounded-full shadow-md transition-transform transform hover:scale-105 flex items-center gap-2">
                Try the Simulator <ArrowRightIcon className="w-4 h-4" />
            </button>
        </div>
    </div>
);

const ResourceCard: React.FC<{ resource: typeof allResources[0] }> = ({ resource }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const isLoanType = resource.category === 'Loan Type';
  
  const Icon = isLoanType 
    ? <LoanIcon className="w-6 h-6 text-blue-600" /> 
    : <ProgramIcon className="w-6 h-6 text-green-600" />;
    
  const iconBg = isLoanType ? 'bg-blue-50' : 'bg-green-50';
  const tagColor = isLoanType ? 'text-blue-600' : 'text-green-600';

  return (
    <div className="bg-white border border-gray-200 rounded-xl transition-shadow hover:shadow-lg h-full flex flex-col">
      <div className="p-6 flex-grow">
        <div className="flex items-start gap-4">
          <div className={`${iconBg} p-3 rounded-lg flex-shrink-0`}>{Icon}</div>
          <div>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${tagColor}`}>{resource.category}</span>
            <h3 className="font-bold text-gray-900 mt-1 leading-tight">{resource.title}</h3>
            <p className="text-sm text-gray-600 mt-2">{resource.description}</p>
          </div>
        </div>
      </div>
      <div className="px-6">
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[500px] pb-4' : 'max-h-0'}`}>
          <div className="border-t border-gray-200 pt-4 space-y-3">
             {resource.details.map((item: any, index) => {
                if (item.type === 'heading') return <h4 key={index} className="text-sm font-semibold text-gray-800">{item.text}</h4>;
                if (item.type === 'bullet') return <p key={index} className="text-sm text-gray-600 pl-4 relative before:content-['•'] before:absolute before:left-0">{item.text}</p>;
                if (item.type === 'link') return <a key={index} href={item.href} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-green-600 hover:underline flex items-center gap-1">{item.text} <span aria-hidden="true">&rarr;</span></a>;
                if (item.type === 'download') return (
                    <a key={index} href={item.href} className="text-sm font-semibold text-blue-600 hover:underline flex items-center gap-2">
                        <DownloadIcon className="w-4 h-4" /> {item.text}
                    </a>
                );
                return null;
             })}
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 p-4 flex justify-between items-center text-xs mt-auto bg-gray-50/50 rounded-b-xl">
          <span className="text-gray-500">Updated: {resource.lastUpdated}</span>
          <button onClick={() => setIsOpen(!isOpen)} className="font-semibold text-gray-700 hover:text-gray-900 flex items-center gap-1">
            {isOpen ? 'Show Less' : 'Learn More'}
            <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </button>
      </div>
    </div>
  );
};

// --- Main Page Component ---

const ResourcesPage: React.FC<{ userData?: any }> = ({ userData }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<typeof allResources>(allResources);
    const [activeTab, setActiveTab] = useState<'all' | 'loan' | 'program' | 'faq' | 'tools'>('all');

    // SCROLL RESTORATION
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab]);

    // Trigger vector search when user stops typing or hits enter
    const handleSearch = async () => {
        if (!searchTerm) {
            setSearchResults(allResources);
            return;
        }

        setIsSearching(true);
        // Transform resources into document shape for vector service
        const docs = allResources.map(r => ({
            id: r.id,
            content: `${r.title} ${r.description} ${r.category} ${JSON.stringify(r.details)}`,
            metadata: r
        }));

        const results = await performSemanticSearch(searchTerm, docs);
        
        // Map back to full resource objects
        const matchedResources = allResources.filter(r => results.some(res => res.id === r.id));
        setSearchResults(matchedResources);
        setIsSearching(false);
    };

    const filteredResources = useMemo(() => {
        return searchResults.filter(resource => {
            const matchesTab = activeTab === 'all' || 
                               (activeTab === 'loan' && resource.category === 'Loan Type') ||
                               (activeTab === 'program' && resource.category === 'Forgiveness Program');
            return matchesTab;
        });
    }, [searchResults, activeTab]);

    const filteredFaqs = useMemo(() => {
        return faqs.filter(faq => {
            const matchesTab = activeTab === 'all' || activeTab === 'faq';
            // Fallback string match for FAQs since we only vectorized resources for this demo
            const matchesSearch = searchTerm === '' || 
                                  faq.q.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                  faq.a.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesTab && matchesSearch;
        });
    }, [searchTerm, activeTab]);
    
    const TabButton: React.FC<{tab: typeof activeTab, label: string}> = ({ tab, label }) => (
        <button
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                activeTab === tab 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="max-w-7xl mx-auto">
            {/* Hero Section - Redesigned to match screenshot */}
            <div className="text-center mb-10 py-16 bg-[#EFF6FF] rounded-3xl relative overflow-hidden mx-4 sm:mx-0">
                <div className="relative z-10 px-4">
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-4">
                        Your Student Loan <span className="text-blue-600">Knowledge Base</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Everything you need to know about federal, private, and specialty loan forgiveness—all in one spot.
                    </p>
                </div>
            </div>

            {/* Filter Bar & Search - Redesigned to match screenshot */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between max-w-7xl mx-auto mb-10 px-4 sm:px-0">
                {activeTab !== 'tools' && (
                    <div className="relative w-full md:w-96">
                        <input
                            type="search"
                            placeholder="Search programs, loans, or FAQs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            onBlur={handleSearch} // Trigger search on blur
                            className="w-full py-3 pl-10 pr-10 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500 transition-all text-sm shadow-sm text-gray-700"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <SearchIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        {isSearching && (
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <SpinnerIcon className="w-4 h-4 text-blue-600 animate-spin" />
                            </div>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap items-center justify-center gap-2">
                    <TabButton tab="all" label="All" />
                    <TabButton tab="program" label="Forgiveness Programs" />
                    <TabButton tab="loan" label="Loan Types" />
                    <TabButton tab="faq" label="FAQs" />
                    <TabButton tab="tools" label="Tools" />
                </div>
            </div>
            
            {activeTab === 'tools' ? (
                <div className="max-w-5xl mx-auto space-y-12 pb-12">
                    <InterestSimulator userData={userData} />
                    <DocumentIntelligence />
                </div>
            ) : (
                <>
                    {/* Featured Tool Card - Only shows on 'all' tab if no search active */}
                    {(activeTab === 'all' && !searchTerm) && (
                        <FeaturedToolCard onClick={() => setActiveTab('tools')} />
                    )}

                    {(filteredResources.length === 0 && filteredFaqs.length === 0 && !isSearching) ? (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                            <BookOpenIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <h3 className="text-xl font-semibold text-gray-700">No Matches Found</h3>
                            <p className="text-gray-500 mt-2">We couldn't find what you were looking for. Try rephrasing your search.</p>
                            <button onClick={() => {setSearchTerm(''); setSearchResults(allResources); setActiveTab('all')}} className="mt-4 text-blue-600 font-semibold hover:underline">Clear Search</button>
                        </div>
                    ) : (
                        <div className="space-y-12 pb-12">
                            {filteredResources.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filteredResources.map(resource => <ResourceCard key={resource.id} resource={resource} />)}
                                </div>
                            )}
                            
                            {filteredFaqs.length > 0 && (
                                <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100">
                                    <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                                        <div className="bg-blue-100 p-2 rounded-lg">
                                            <BookOpenIcon className="w-6 h-6 text-blue-600" />
                                        </div>
                                        Frequently Asked Questions
                                    </h2>
                                    <div className="space-y-4 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1">
                                        {filteredFaqs.map((faq, index) => <FAQItem key={index} question={faq.q} answer={faq.a} />)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ResourcesPage;
